import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHero } from '@/components/ui/PageHero'
import { QrCode } from '@/components/ui/QrCode'
import { ConfirmModal } from '@/components/ui/ConfirmModal'
import { useToast } from '@/contexts/ToastContext'
import { formatNumber } from '@/lib/format'
import { cn } from '@/lib/cn'
import { WALLET_ASSETS } from './assets'
import { BANK_CURRENCIES } from './bank'
import { MethodTabs, type WalletMethod } from './MethodTabs'

/**
 * Withdraw flow — Crypto or Bank transfer.
 */
export function WithdrawPage({ className }: { className?: string }) {
  const { t } = useTranslation('common')
  const { pushToast } = useToast()
  const [method, setMethod] = useState<WalletMethod>('crypto')

  return (
    <div
      className={cn(
        'terminal-scroll h-full min-h-0 overflow-y-auto',
        className,
      )}
    >
      <PageHero
        tone="ask"
        glow="end"
        eyebrow={t('wallet.withdrawEyebrow')}
        title={t('wallet.withdrawTitle')}
        subtitle={t('wallet.withdrawSubtitle')}
      />

      <div className="mx-auto max-w-5xl space-y-4 p-4 sm:p-6">
        <MethodTabs value={method} onChange={setMethod} />
        {method === 'crypto' ? (
          <CryptoWithdraw pushToast={pushToast} />
        ) : (
          <BankWithdraw pushToast={pushToast} />
        )}
      </div>
    </div>
  )
}

type ToastFn = (t: {
  tone?: 'success' | 'danger'
  title: string
  message?: string
}) => void

function CryptoWithdraw({ pushToast }: { pushToast: ToastFn }) {
  const { t } = useTranslation('common')
  const [assetId, setAssetId] = useState(WALLET_ASSETS[0].id)
  const asset = WALLET_ASSETS.find((a) => a.id === assetId) ?? WALLET_ASSETS[0]
  const [networkId, setNetworkId] = useState(asset.networks[0].id)
  const network =
    asset.networks.find((n) => n.id === networkId) ?? asset.networks[0]
  const [address, setAddress] = useState('')
  const [amount, setAmount] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const amountNum = Number(amount) || 0
  const fee = useMemo(() => {
    if (asset.symbol === 'USDT') return network.id === 'erc20' ? 5 : 1
    if (asset.symbol === 'BTC') return 0.00005
    return 0.001
  }, [asset.symbol, network.id])
  const receive = Math.max(0, amountNum - fee)
  const canSubmit =
    address.trim().length >= 12 && amountNum > 0 && amountNum <= asset.balance

  const onAsset = (id: string) => {
    setAssetId(id)
    const next = WALLET_ASSETS.find((a) => a.id === id)
    if (next) setNetworkId(next.networks[0].id)
  }

  const submit = async () => {
    setSubmitting(true)
    await new Promise((r) => setTimeout(r, 700))
    setSubmitting(false)
    setConfirmOpen(false)
    setAddress('')
    setAmount('')
    pushToast({
      tone: 'success',
      title: t('wallet.withdrawSubmitted'),
      message: t('wallet.withdrawSubmittedDesc'),
    })
  }

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <div className="flex flex-col gap-4">
          <section className="rounded-xl border border-terminal-border/40 bg-terminal-surface p-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
                {t('wallet.selectAsset')}
              </h2>
              <span className="font-mono text-[11px] tabular-nums text-terminal-muted">
                {t('wallet.available')}:{' '}
                <span className="font-semibold text-terminal-fg">
                  {formatNumber(asset.balance, { maximumFractionDigits: 6 })}{' '}
                  {asset.symbol}
                </span>
              </span>
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {WALLET_ASSETS.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onAsset(a.id)}
                  className={cn(
                    'flex min-w-[4.5rem] flex-col items-start rounded-lg border px-3 py-2',
                    a.id === asset.id
                      ? 'border-accent/50 bg-accent/10 text-accent'
                      : 'border-terminal-border/40 bg-terminal-bg text-terminal-muted',
                  )}
                >
                  <span className="text-xs font-semibold">{a.symbol}</span>
                  <span className="text-[10px] opacity-70">{a.name}</span>
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {asset.networks.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => setNetworkId(n.id)}
                  className={cn(
                    'h-8 rounded-lg border px-2.5 text-[11px] font-semibold',
                    n.id === network.id
                      ? 'border-accent bg-accent text-white'
                      : 'border-terminal-border/40 text-terminal-muted',
                  )}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </section>

          <section className="rounded-xl border border-terminal-border/40 bg-terminal-surface p-4">
            <label className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
              {t('wallet.destination')}
            </label>
            <textarea
              value={address}
              onChange={(e) => setAddress(e.target.value.trim())}
              rows={2}
              spellCheck={false}
              placeholder={t('wallet.destinationPlaceholder')}
              className={cn(
                'mt-1.5 w-full resize-none rounded-lg border border-terminal-border/40 bg-terminal-bg',
                'px-3 py-2.5 font-mono text-xs text-terminal-fg',
                'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
              )}
            />
            <div className="mt-3 flex items-end gap-2">
              <label className="min-w-0 flex-1">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
                  {t('wallet.amount')}
                </span>
                <input
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  inputMode="decimal"
                  className={cn(
                    'mt-1.5 h-10 w-full rounded-lg border border-terminal-border/40 bg-terminal-bg',
                    'px-3 font-mono text-sm tabular-nums',
                    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
                  )}
                />
              </label>
              <button
                type="button"
                onClick={() => setAmount(String(asset.balance))}
                className="h-10 shrink-0 rounded-lg border border-accent/40 px-3 text-xs font-semibold text-accent hover:bg-accent/10"
              >
                {t('wallet.max')}
              </button>
            </div>
            <dl className="mt-3 space-y-1.5 border-t border-terminal-border/40 pt-3 text-[11px]">
              <div className="flex justify-between text-terminal-muted">
                <dt>{t('wallet.networkFee')}</dt>
                <dd className="font-mono text-terminal-fg">
                  {formatNumber(fee, { maximumFractionDigits: 6 })} {asset.symbol}
                </dd>
              </div>
              <div className="flex justify-between text-terminal-muted">
                <dt>{t('wallet.youReceive')}</dt>
                <dd className="font-mono text-sm font-semibold text-bid">
                  {formatNumber(receive, { maximumFractionDigits: 6 })}{' '}
                  {asset.symbol}
                </dd>
              </div>
            </dl>
            <button
              type="button"
              disabled={!canSubmit}
              onClick={() => setConfirmOpen(true)}
              className="mt-4 h-11 w-full rounded-lg bg-accent text-sm font-semibold text-white hover:bg-accent-muted disabled:opacity-40"
            >
              {t('wallet.reviewWithdraw')}
            </button>
          </section>
        </div>

        <div className="flex flex-col items-center rounded-xl border border-terminal-border/40 bg-terminal-surface p-5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
            {t('wallet.verifyQr')}
          </p>
          <p className="mt-1 text-center text-xs text-terminal-muted">
            {t('wallet.verifyQrHint')}
          </p>
          <div className="mt-4">
            {address.trim().length >= 12 ? (
              <QrCode value={address.trim()} size={168} />
            ) : (
              <div
                className="flex items-center justify-center rounded-xl border border-dashed border-terminal-border/60 bg-terminal-bg text-[11px] text-terminal-muted"
                style={{ width: 192, height: 192 }}
              >
                {t('wallet.qrWaiting')}
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={t('wallet.confirmWithdrawTitle')}
        message={t('wallet.confirmWithdrawMessage', {
          amount: formatNumber(amountNum, { maximumFractionDigits: 6 }),
          asset: asset.symbol,
          network: network.label,
        })}
        tone="danger"
        loading={submitting}
        confirmLabel={t('wallet.confirmWithdraw')}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void submit()}
      />
    </>
  )
}

function BankWithdraw({ pushToast }: { pushToast: ToastFn }) {
  const { t } = useTranslation('common')
  const [currency, setCurrency] = useState<(typeof BANK_CURRENCIES)[number]>('USD')
  const [iban, setIban] = useState('')
  const [beneficiary, setBeneficiary] = useState('')
  const [swift, setSwift] = useState('')
  const [amount, setAmount] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const amountNum = Number(amount) || 0
  const fee = currency === 'USD' ? 15 : 12
  const receive = Math.max(0, amountNum - fee)
  const canSubmit =
    iban.replace(/\s/g, '').length >= 15 &&
    beneficiary.trim().length >= 2 &&
    amountNum > 0

  const submit = async () => {
    setSubmitting(true)
    await new Promise((r) => setTimeout(r, 700))
    setSubmitting(false)
    setConfirmOpen(false)
    setIban('')
    setBeneficiary('')
    setSwift('')
    setAmount('')
    pushToast({
      tone: 'success',
      title: t('wallet.withdrawSubmitted'),
      message: t('wallet.withdrawSubmittedDesc'),
    })
  }

  const fieldClass = cn(
    'mt-1.5 h-10 w-full rounded-lg border border-terminal-border/40 bg-terminal-bg',
    'px-3 text-xs text-terminal-fg',
    'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-accent',
  )

  return (
    <>
      <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
        <section className="rounded-xl border border-terminal-border/40 bg-terminal-surface p-4 sm:p-5">
          <h2 className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
            {t('wallet.bankWithdrawForm')}
          </h2>

          <div className="mt-3 flex flex-wrap gap-1.5">
            {BANK_CURRENCIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCurrency(c)}
                className={cn(
                  'h-8 rounded-lg border px-3 text-[11px] font-semibold',
                  c === currency
                    ? 'border-accent bg-accent text-white'
                    : 'border-terminal-border/40 text-terminal-muted',
                )}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="sm:col-span-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
                {t('wallet.bankBeneficiary')}
              </span>
              <input
                value={beneficiary}
                onChange={(e) => setBeneficiary(e.target.value)}
                className={fieldClass}
              />
            </label>
            <label className="sm:col-span-2">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
                {t('wallet.bankIban')}
              </span>
              <input
                value={iban}
                onChange={(e) => setIban(e.target.value.toUpperCase())}
                className={cn(fieldClass, 'font-mono')}
                spellCheck={false}
              />
            </label>
            <label>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
                {t('wallet.bankSwift')}
              </span>
              <input
                value={swift}
                onChange={(e) => setSwift(e.target.value.toUpperCase())}
                className={cn(fieldClass, 'font-mono')}
                spellCheck={false}
              />
            </label>
            <label>
              <span className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
                {t('wallet.amount')}
              </span>
              <input
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                inputMode="decimal"
                className={cn(fieldClass, 'font-mono')}
              />
            </label>
          </div>

          <dl className="mt-4 space-y-1.5 border-t border-terminal-border/40 pt-3 text-[11px]">
            <div className="flex justify-between text-terminal-muted">
              <dt>{t('wallet.bankTransferFee')}</dt>
              <dd className="font-mono text-terminal-fg">
                {fee} {currency}
              </dd>
            </div>
            <div className="flex justify-between text-terminal-muted">
              <dt>{t('wallet.youReceive')}</dt>
              <dd className="font-mono text-sm font-semibold text-bid">
                {formatNumber(receive, { maximumFractionDigits: 2 })} {currency}
              </dd>
            </div>
          </dl>

          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => setConfirmOpen(true)}
            className="mt-4 h-11 w-full rounded-lg bg-accent text-sm font-semibold text-white hover:bg-accent-muted disabled:opacity-40"
          >
            {t('wallet.reviewWithdraw')}
          </button>
        </section>

        <div className="rounded-xl border border-terminal-border/40 bg-terminal-surface p-5">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-terminal-muted">
            {t('wallet.bankWithdrawTips')}
          </p>
          <ul className="mt-3 space-y-2.5 text-[11px] text-terminal-muted">
            <li className="flex gap-2">
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              {t('wallet.bankWithdrawTip1')}
            </li>
            <li className="flex gap-2">
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              {t('wallet.bankWithdrawTip2')}
            </li>
            <li className="flex gap-2">
              <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-danger" />
              {t('wallet.bankWithdrawTip3')}
            </li>
          </ul>
          {iban.replace(/\s/g, '').length >= 15 ? (
            <div className="mt-5 flex justify-center">
              <QrCode value={iban.replace(/\s/g, '')} size={140} />
            </div>
          ) : null}
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        title={t('wallet.confirmWithdrawTitle')}
        message={t('wallet.confirmBankWithdrawMessage', {
          amount: formatNumber(amountNum, { maximumFractionDigits: 2 }),
          currency,
        })}
        tone="danger"
        loading={submitting}
        confirmLabel={t('wallet.confirmWithdraw')}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void submit()}
      />
    </>
  )
}
